import { useAuth } from "@clerk/clerk-react";
import { useCallback, useEffect, useState } from "react";

/**
 * Per-org `/onboarding` flow preference. `"argus"` (default) renders the
 * v2 hero + agent flow; `"manual"` opts the user into the legacy
 * `<BetterOnboarding />` wizard. Persisted in localStorage so the choice
 * survives reloads — kept off the BE since it's purely a per-device UI
 * preference.
 *
 * Flag-completion semantics are unchanged: both modes ultimately write
 * `flags.onboarding.completed_at` (the auto-complete BE listener watches
 * for AppConfig + Test inserts regardless of which UI flow created them).
 */

export type OnboardingMode = "argus" | "manual";

const STORAGE_PREFIX = "desplega-onboarding-mode";
const DEFAULT_MODE: OnboardingMode = "argus";

// Same-window broadcast channel: the native `storage` event fires only for
// OTHER windows/tabs, so two `useOnboardingMode()` instances mounted in the
// same page (e.g. the toggling button + the parent that branches on mode)
// won't see each other's writes without an explicit pump. We dispatch a
// CustomEvent on every setMode so all live instances re-read.
const SAME_WINDOW_EVENT = "desplega-onboarding-mode-changed";

const storageKey = (orgId: string | null | undefined) =>
  `${STORAGE_PREFIX}-${orgId ?? "anonymous"}`;

const readMode = (orgId: string | null | undefined): OnboardingMode => {
  if (typeof window === "undefined") return DEFAULT_MODE;
  try {
    const raw = localStorage.getItem(storageKey(orgId));
    return raw === "manual" ? "manual" : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
};

export function useOnboardingMode(): [OnboardingMode, (next: OnboardingMode) => void] {
  const { orgId } = useAuth();
  const [mode, setModeState] = useState<OnboardingMode>(() => readMode(orgId));

  // Re-sync when the active org changes (Clerk OrgSwitcher).
  useEffect(() => {
    setModeState(readMode(orgId));
  }, [orgId]);

  // Re-sync on same-window broadcast (other instance flipped the mode) and
  // on cross-tab `storage` events (user has the app open in two tabs).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setModeState(readMode(orgId));
    window.addEventListener(SAME_WINDOW_EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(SAME_WINDOW_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, [orgId]);

  const setMode = useCallback(
    (next: OnboardingMode) => {
      try {
        localStorage.setItem(storageKey(orgId), next);
      } catch {
        // Storage may be unavailable (private mode, quota); state still updates
        // so the current tab reflects the choice.
      }
      setModeState(next);
      try {
        window.dispatchEvent(new Event(SAME_WINDOW_EVENT));
      } catch {
        // dispatchEvent only fails in non-browser envs we don't target.
      }
    },
    [orgId],
  );

  return [mode, setMode];
}
